// Imports
import { Add, AddCircle, ArrowBack, LockOpenOutlined, LockOutlined, MenuBookOutlined, RemoveCircle } from '@mui/icons-material';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Fab, IconButton, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getHubSelection } from '../../features/hubSelection';
import { GobikesApi } from '../../config/Apis';
import { parseErrorForAxios } from '../utils/CommonUtils';
import { getAuthCookie } from '../auth/AuthUtil';
import { BikeModel, BlockModel } from '../utils/Models';
import { Colors, DEFAULT_LOADER_STYLES, NavTabIndices } from '../../config/Constants';
import { BlocksTable } from './BlocksTable';
import { GbkFieldType, GobikesField } from '../utils/GobikesField';
import { getVendorProfile } from '../../features/vendorProfile';
import { DateTime } from 'luxon';
import { LoadingButton } from '@mui/lab';
import { DashboardLoader } from '../utils/LocalStateUtils';
import { setNavSelection } from '../../features/navSelection';

// Types and constants
type BlockView = 'all' | 'create';
export const defaultBlockView = 'all';
interface LocalState {
  view: BlockView;
  loading?: boolean;
  error?: boolean;
  message?: string;
  blockData?: BlockModel;
  blockListLoadRequired?: boolean;
}
const DEFAULT_QUANITTY_VALUE = 1;

// Main app component
export function ManageBlocks(props: {
  window?: () => Window;
  drawerWidth: number
}) {
  // Local state
  const [state, setState] = useState<LocalState>({ view: defaultBlockView });
  const [blocksData, setBlocksData] = useState<BlockModel[]>([]);
  const [blockAllModal, setBlockAllModal] = useState<{ open: boolean, blockStart?: number, blockEnd?: number, saving?: boolean, error?: string }>({ open: false });
  const [formState, setFormState] = useState<{
    from?: number,
    to?: number,
    bikeQtyMap: { [k: number]: number },
    bikeId?: number,
    quantity?: number,
    availableQuantity?: number,
    availableBikesList?: BikeModel[],
    dataLoadRequired?: boolean
    loading?: boolean,
    saving?: boolean;
    formError?: string;
  }>({ quantity: DEFAULT_QUANITTY_VALUE, bikeQtyMap: {} });

  // Local variables based on state
  const createBlockView = state.view === 'create';

  // Global state
  const selectedHub = useSelector(getHubSelection);
  const profile = useSelector(getVendorProfile);
  const authCookie = getAuthCookie();
  const theme = useTheme();
  const isPhone = !useMediaQuery(theme.breakpoints.up('md'));
  const dispatch = useDispatch();

  // Set nav state
  useEffect(() => {
    dispatch(setNavSelection(NavTabIndices.BLOCKS));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load blocks on first load
  const loadBlocks = () => {
    if (!selectedHub?.id || !authCookie) return;
    setState({ loading: true, view: defaultBlockView });
    (async () => {
      try {
        const { data } = await GobikesApi.get(`/vendors/hubs/${selectedHub.id}/blocks`, { params: { sendAvailableQty: true, futureBlocksOnly: true }, headers: { 'vendor-token': authCookie } })
        const blocksList: BlockModel[] = data.data;
        setState({ view: defaultBlockView });
        setBlocksData(blocksList);
      } catch (e) {
        setState({ error: true, message: parseErrorForAxios(e), view: defaultBlockView });
      }
    })();
  }
  useEffect(loadBlocks, [selectedHub]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (state.blockListLoadRequired) loadBlocks(); }, [state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Available bike list
  const getAvailableBikesList = () => {
    if (formState.dataLoadRequired && formState.from && formState.to) {
      (async () => {
        try {
          const { data } = await GobikesApi.get(`bikes/all`, {
            headers: { 'vendor-token': authCookie },
            params: { city: profile.cityId, from: formState.from, to: formState.to, minimalData: true, ignoreMinDuration: true, ignorePrepTime: true, ignoreOpTime: true, ignorePriceAvailability: true, quantity: true, hubs: selectedHub.id }
          })
          const bikeList: BikeModel[] = data.data.bikes;
          setFormState({ ...formState, bikeId: undefined, availableBikesList: bikeList, availableQuantity: undefined, dataLoadRequired: false, loading: false });
        } catch (e) {
          setState({ error: true, message: parseErrorForAxios(e), view: defaultBlockView });
        }
      })();
    }
  }
  useEffect(getAvailableBikesList, [formState]);// eslint-disable-line react-hooks/exhaustive-deps

  // Back button handler for error states
  async function resetError() {
    setState({ ...state, error: false });
  }

  // Create block
  async function createBlock() {

    // Parse input and check for errors
    const payload = {
      blockStart: formState.from,
      blockEnd: formState.to,
      blockDetails: Object.keys(formState.bikeQtyMap).filter(id => formState.bikeQtyMap[Number(id)]).map((id) => ({ bikeId: id, quantity: formState.bikeQtyMap[Number(id)] })),
    };
    if (!payload.blockStart || !payload.blockEnd) {
      setFormState({ ...formState, formError: 'Start and end dates are required!' });
      return;
    }
    if (!payload.blockDetails.length) {
      setFormState({ ...formState, formError: 'Quantity is required in at least one bike!' });
      return;
    }

    // Set loading mode and try to save bike
    setFormState({ ...formState, saving: true })
    try {
      await GobikesApi.post(`/vendors/hubs/${selectedHub.id}/blocks/bulk`, payload, { headers: { 'vendor-token': authCookie } });
      setFormState({ loading: false, saving: false, bikeQtyMap: {} });
      setState({ error: false, message: 'Block added successfully!', loading: false, view: defaultBlockView, blockListLoadRequired: true });
    } catch (e: any) {
      const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
      setFormState({ formError: errorMessage, loading: false, ...formState, saving: false });
    }
  }

  // Update block quantity
  async function updateBlockQuantity(blockId: number, newQuantity: number) {
    setState({ loading: true, view: defaultBlockView });
    try {
      await GobikesApi.put(`/vendors/hubs/${selectedHub.id}/blocks/${blockId}`, { quantity: newQuantity }, { headers: { 'vendor-token': authCookie } });
      setState({ error: false, message: 'Block updated successfully!', loading: false, view: defaultBlockView, blockListLoadRequired: true })
    } catch (e: any) {
      setState({ error: true, message: parseErrorForAxios(e), view: defaultBlockView });
    }
  }

  // Block all vehicles of the hub till a given time
  async function blockAllVehicles() {
    if (!blockAllModal.blockStart || !blockAllModal.blockEnd) {
      setBlockAllModal({ ...blockAllModal, error: 'Start and end date/time are required!' });
      return;
    }
    if (blockAllModal.blockEnd <= blockAllModal.blockStart) {
      setBlockAllModal({ ...blockAllModal, error: 'End date/time must be after start date/time!' });
      return;
    }
    setBlockAllModal({ ...blockAllModal, saving: true });
    try {
      await GobikesApi.post(`/vendors/hubs/${selectedHub.id}/blocks/all`, { blockStart: blockAllModal.blockStart, blockEnd: blockAllModal.blockEnd }, { headers: { 'vendor-token': authCookie } });
      setBlockAllModal({ open: false });
      setState({ error: false, message: 'All vehicles blocked successfully!', loading: false, view: defaultBlockView, blockListLoadRequired: true });
    } catch (e: any) {
      setBlockAllModal({ ...blockAllModal, saving: false, error: parseErrorForAxios(e) });
    }
  }

  // Unblock all vehicles of the hub
  async function unblockAllVehicles() {
    setState({ loading: true, view: defaultBlockView });
    try {
      await GobikesApi.delete(`/vendors/hubs/${selectedHub.id}/blocks/all`, { headers: { 'vendor-token': authCookie } });
      setState({ error: false, message: 'All vehicles unblocked successfully!', loading: false, view: defaultBlockView, blockListLoadRequired: true });
    } catch (e: any) {
      setState({ error: true, message: parseErrorForAxios(e), view: defaultBlockView });
    }
  }

  // Loading view
  let mainContent = <div></div>;
  if (state.loading) {
    mainContent = <DashboardLoader
      {...state}
      styles={DEFAULT_LOADER_STYLES}
    />
  }

  // Error view
  else if (state.error) {
    mainContent = <DashboardLoader
      {...state}
      styles={DEFAULT_LOADER_STYLES}
      resetError={resetError}
    />
  }

  // All blocks view
  else if (state.view === 'all') {
    mainContent = <Fragment>
      <Box className='gbk-tabs' sx={{ typography: 'body1', m: '30px 0 0 0' }}>
        <Box className="phone-fab">
          <Button className="thick-border" onClick={() => { setState({ view: 'create' }) }} variant="outlined"><Add /> Add Block</Button>
        </Box>
        <Box sx={{ display: { xs: 'grid', md: 'none' }, gridTemplateColumns: '1fr 1fr', gap: '10px', p: '12px 10px 4px', boxSizing: 'border-box', borderBottom: '1px solid #eee' }}>
          {renderBlockAllButtons({ minWidth: 0, whiteSpace: 'nowrap', px: 1 })}
        </Box>
        <BlocksTable
          blocksList={blocksData}
          setParentState={setState}
          updateBlockQuanity={updateBlockQuantity}
          containerClass={'gbk-grid-parent no-fabs'}
        />
      </Box >
    </Fragment>
  }

  // Individual block view
  else if (state.view === 'create') {
    mainContent = (
      <Fragment>
        <div className="gbk-paper-container">
          {/* Empty space at top for phone screen */}
          <Fragment>
            <Box sx={{ display: { xs: 'block', md: 'none' }, height: { xs: '18px', sm: '26px' }, width: 1 }}><br /></Box>
          </Fragment>
          {renderCreateBlockForm()}<br /><br />
        </div>

        {/* Action buttons */}
        <Box sx={{
          position: 'fixed',
          bottom: 0,
          left: { xs: 0, md: props.drawerWidth },
          width: { xs: '100vh', md: `calc(100vw - ${props.drawerWidth}px)` },
          padding: '20px 40px',
          paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
          background: '#fff',
          boxShadow: '0px -2px 6px #ddd'
        }}>
          <LoadingButton variant="contained" color="primary" onClick={createBlock} loading={formState.saving} >Create</LoadingButton>
          <Button className="thick-border" variant="outlined" sx={{ marginLeft: 2 }} onClick={() => { setState({ view: defaultBlockView, blockData: undefined }) }}>Cancel</Button>
          {isPhone && formState.formError ? <br /> : ''}
          <Typography className='gbk-message error' sx={{ display: 'inline-block', paddingLeft: { xs: 0, md: '20px' } }}> {formState.formError} </Typography>
        </Box>

      </Fragment >
    )
  }

  // Default view
  else {
    mainContent = <div></div>;
  }

  // Return DOM
  return (
    <Box sx={{ mt: { xs: '50px', md: 0 }, ml: { xs: 0, md: `${props.drawerWidth}px` }, width: { xs: '100%', md: `calc(100% - ${props.drawerWidth}px)` }, height: '100%' }}>
      <div className={`gbk-tabcontent ${state.view === 'all' ? '' : 'allow-scroll'}`}>

        {/* Add block fab */}
        <Fab onClick={() => { setState({ view: 'create' }) }} className='gbk-fab gbk-fab-top-right' sx={{ display: { xs: 'none', md: 'inline-flex' } }} variant='extended' color='primary' > <Add /> Add Block</Fab>

        {/* Block/unblock all vehicles (desktop) */}
        {state.view === 'all' && (
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, position: 'absolute', top: '40px', right: '210px' }}>
            {renderBlockAllButtons({ height: 48, borderRadius: '24px', px: 3, whiteSpace: 'nowrap' })}
          </Box>
        )}

        {/* Header */}
        <Box className='tab-header' sx={{ display: { xs: 'none', md: 'block' } }}>

          {/* Icon */}
          <div
            className={`tab-header-fab ${createBlockView ? 'clickable' : ''}`}
            onClick={() => { if (createBlockView) setState({ view: defaultBlockView, blockData: undefined }) }} >
            {
              createBlockView
                ? <ArrowBack fontSize="large" />
                : <MenuBookOutlined fontSize="large" />
            }
          </div>

          {/* Text */}
          <Typography>
            <span style={{ ...(createBlockView && { color: Colors.primary }) }}>
              Blocks
            </span>
            {createBlockView ? ' / Create Block' : null}
          </Typography>
        </Box>

        {/* Main content */}
        {mainContent}

      </div >

      {/* Block all vehicles dialog */}
      <Dialog open={blockAllModal.open} onClose={() => setBlockAllModal({ open: false })} maxWidth="xs" fullWidth>
        <DialogTitle>Block All Vehicles</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>All vehicles at this hub will be blocked between the selected start and end time. Vehicles already booked in that period are blocked only for the time they are free.</Typography>
          <GobikesField
            type={GbkFieldType.DATE_TIME}
            label={'Block from'}
            editMode={true}
            minDateTime={DateTime.now().set({ minute: 0 })}
            value={blockAllModal.blockStart ? DateTime.fromMillis(blockAllModal.blockStart) : null}
            updateFn={v => { setBlockAllModal({ ...blockAllModal, error: undefined, blockStart: DateTime.fromMillis(v).toMillis() }) }}
          />
          <br />
          <GobikesField
            type={GbkFieldType.DATE_TIME}
            label={'Block till'}
            editMode={true}
            minDateTime={blockAllModal.blockStart ? DateTime.fromMillis(blockAllModal.blockStart).plus({ hours: 1 }) : DateTime.now().plus({ hours: 1 }).set({ minute: 0 })}
            value={blockAllModal.blockEnd ? DateTime.fromMillis(blockAllModal.blockEnd) : null}
            updateFn={v => { setBlockAllModal({ ...blockAllModal, error: undefined, blockEnd: DateTime.fromMillis(v).toMillis() }) }}
          />
          {blockAllModal.error ? <Typography className='gbk-message error'>{blockAllModal.error}</Typography> : ''}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockAllModal({ open: false })}>Cancel</Button>
          <LoadingButton variant="contained" color="primary" loading={blockAllModal.saving} onClick={blockAllVehicles}>Block All</LoadingButton>
        </DialogActions>
      </Dialog>
    </Box >
  )

  function renderBlockAllButtons(buttonSx: object) {
    return <Fragment>
      <Button variant="contained" color="primary" sx={buttonSx} startIcon={<LockOutlined />} onClick={() => { setBlockAllModal({ open: true }) }}>
        Block All<Box component="span" sx={{ display: { xs: 'none', md: 'inline' }, whiteSpace: 'pre' }}> Vehicles</Box>
      </Button>
      <Button className="thick-border" variant="outlined" sx={buttonSx} startIcon={<LockOpenOutlined />} onClick={unblockAllVehicles}>
        Unblock All<Box component="span" sx={{ display: { xs: 'none', md: 'inline' }, whiteSpace: 'pre' }}> Vehicles</Box>
      </Button>
    </Fragment>
  }

  // Create block form
  function renderCreateBlockForm() {
    return <Fragment>

      {/* Block timings */}
      <Paper className="gbk-paper" sx={{ width: '100%' }} elevation={0}>
        <div className="header">
          <Typography className="header-left" fontSize={18} fontWeight={500}>Block Timings</Typography>
        </div>
        <div className="content" >
          <div className="gbk-col" style={{ minWidth: 400 }}>
            <GobikesField
              type={GbkFieldType.DATE_TIME}
              label={'Start date'}
              editMode={true}
              minDateTime={DateTime.now().plus({ hours: 1 }).set({ minute: 0 })}
              value={formState.from ? DateTime.fromMillis(formState.from) : null}
              updateFn={v => { setFormState({ ...formState, loading: !!formState.to && !!v, dataLoadRequired: true, from: DateTime.fromMillis(v).startOf('hour').toMillis() }) }}
            />
          </div>
          <div className="gbk-col" style={{ minWidth: 400 }}>
            <GobikesField
              type={GbkFieldType.DATE_TIME}
              label={'End date'}
              editMode={true}
              minDateTime={formState.from ? DateTime.fromMillis(formState.from).plus({ hours: 1 }) : DateTime.now().plus({ hours: 1 }).set({ minute: 0 })}
              value={formState.to ? DateTime.fromMillis(formState.to) : null}
              updateFn={v => { setFormState({ ...formState, loading: !!formState.from && !!v, dataLoadRequired: true, to: DateTime.fromMillis(v).startOf('hour').toMillis() }) }}
            />
          </div>
        </div>
      </Paper>
      <br /><br />

      {/* Bike details */}
      <Paper className="gbk-paper" sx={{ width: '100%' }} elevation={0}>
        <div className="header">
          <Typography className="header-left" fontSize={18} fontWeight={500}>Vehicle Details</Typography>
        </div>
        <div className="content" >
          {
            formState.loading ? (
              <Box textAlign='center'>
                <CircularProgress />
                <br /><br />
                <Typography>Loading bikes...</Typography>
              </Box>
            ) : (
              <Fragment>
                {formState.availableBikesList ? '' : <Typography>Select dates to enable bike selection!</Typography>}
                {(formState.availableBikesList || []).map((bike: BikeModel, i) => (
                  <Box sx={{ width: '600px' }}>
                    <Box className="inb" sx={{ width: { xs: '220px', md: '300px' } }}>
                      <Typography> {bike.brand} {bike.name}</Typography>
                    </Box>
                    <Box className="inb">
                      <IconButton
                        color='primary'
                        onClick={() => {
                          const currentQty = formState.bikeQtyMap[bike.id] || 0;
                          if (currentQty && currentQty > 0) {
                            const newBikeQtyMap = { ...formState.bikeQtyMap };
                            newBikeQtyMap[bike.id] = currentQty - 1;
                            setFormState({ ...formState, bikeQtyMap: newBikeQtyMap })
                          }
                        }}
                      >
                        <RemoveCircle />
                      </IconButton>
                      <Typography className='inb' sx={{ verticalAlign: 'middle' }}>
                        <span style={{ fontWeight: 'bold' }}>{formState.bikeQtyMap[bike.id] ? formState.bikeQtyMap[bike.id] : 0}</span>
                        <span style={{ color: Colors.greyDark3 }}> / {bike.hubs?.[0]?.hub_bikes?.dynamicQuantity || 0}</span>
                      </Typography>
                      <IconButton color='primary'
                        onClick={() => {
                          const currentQty = formState.bikeQtyMap[bike.id] || 0;
                          const maxQty = bike.hubs?.[0]?.hub_bikes?.dynamicQuantity || 0;
                          if (currentQty < maxQty) {
                            const newBikeQtyMap = { ...formState.bikeQtyMap };
                            newBikeQtyMap[bike.id] = currentQty + 1;
                            setFormState({ ...formState, bikeQtyMap: newBikeQtyMap })
                          }
                        }}
                      >
                        <AddCircle />
                      </IconButton>
                    </Box>
                    {i < (formState.availableBikesList || []).length - 1 ? (
                      <Box sx={{ width: { xs: '320px', md: '400px' }, margin: '6px 0 10px 0', borderBottom: '1px solid #ddd' }} ></Box>
                    ) : ''}
                  </Box>
                ))}
              </Fragment>
            )
          }
        </div>
      </Paper>
    </Fragment>
  }

}